import { useState } from "react";
import type { WriteResult } from "@estelle/domain";

import {
  failedItems,
  runBulk,
  type BulkOutcome,
} from "@/components/table/bulk";

/**
 * Runs a bulk write (FR-CAT-8) and tracks its progress and result, with a
 * retry limited to the items that failed.
 *
 * Kept out of `BulkActionBar.tsx` because the HSN action and the tag action
 * are otherwise identical in everything but what `write` does — without
 * this, the progress state, the retry-merge logic and the report would be
 * three copies apart instead of one shared one.
 */
export function useBulkRun<T>(
  write: (item: T) => Promise<WriteResult<unknown>>,
) {
  const [outcomes, setOutcomes] = useState<BulkOutcome<T>[] | null>(null);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [running, setRunning] = useState(false);

  async function runAgainst(items: readonly T[]): Promise<BulkOutcome<T>[]> {
    setProgress({ done: 0, total: items.length });
    setRunning(true);
    try {
      return await runBulk(items, write, {
        onProgress: (done, total) => {
          setProgress({ done, total });
        },
      });
    } finally {
      setRunning(false);
      setProgress(null);
    }
  }

  return {
    outcomes,
    progress,
    running,

    async run(items: readonly T[]) {
      setOutcomes(await runAgainst(items));
    },

    /**
     * Re-attempts only the items that failed last time. Successes from the
     * first pass are kept as they were; each retried item's slot in the
     * report is replaced with its new outcome, in order — so "3 of 12
     * failed, retry" can become "1 of 12 failed" without losing the other
     * eleven's result.
     */
    async retryFailed() {
      if (!outcomes) return;
      const failed = failedItems(outcomes);
      if (failed.length === 0) return;
      const retried = await runAgainst(failed);
      let cursor = 0;
      setOutcomes(
        outcomes.map((outcome) =>
          outcome.status === "success"
            ? outcome
            : (retried[cursor++] ?? outcome),
        ),
      );
    },

    reset() {
      setOutcomes(null);
      setProgress(null);
    },
  };
}
